import React, { useEffect, useRef, useState } from "react";
import { audio } from "services/AudioManager";
import { IMAGES } from "constants/constants";
import GameCityLoading from "components/Shared/GameCityLoading/GameCityLoading";
import { getCachedBillboardUrl, refreshLoadingConfig } from "services/loadingConfig";
import styles from "./Preloader.module.scss";

// Keep the branded screen up briefly even on fast loads so the billboard
// animation reads and the site→game transition doesn't flash.
const MIN_SHOW_MS = 2500;

// On a first-ever visit (no cached billboard) the remote billboard may still
// swap in, but only if it has fetched AND rendered-preloaded this soon after
// mount — late swaps would flash mid-display.
const BILLBOARD_SWAP_WINDOW_MS = 1200;

// Fade-out length; must match .preloader's transition in Preloader.module.scss.
const FADE_MS = 400;

function flatten(obj) {
    if (!obj) return [];
    return Object.values(obj).flatMap(v => (typeof v === "string" ? [v] : flatten(v)));
}

/**
 * App-boot asset preloader on the shared Game City branded loading scene
 * (ported from WWTBAM / TuttiFrutti). Loads priority images, then the rest of
 * the images, fonts and audio one after another. Once everything has resolved
 * AND MIN_SHOW_MS has passed, it calls `onComplete` and fades out.
 *
 * `onComplete` fires at the fade, not when loading finishes: it gates
 * HOST_LOADED in Host.js, which starts the intro, so firing earlier would
 * start the intro under the loader.
 */
export default function Preloader({ imagesToLoad, fontsToLoad, audioToLoad, onComplete }) {
    const [fading, setFading] = useState(false);
    const [done, setDone] = useState(false);
    const [mountedAt] = useState(() => Date.now());
    // Admin-configured billboard: cached value renders from the first frame;
    // refreshLoadingConfig keeps the cache warm for the next loading screen.
    const [billboardSrc, setBillboardSrc] = useState(() => getCachedBillboardUrl());

    // App.js passes a new onComplete each render; read it through a ref so the
    // load pipeline doesn't restart, and fire it at most once.
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;
    const completedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const hadCache = !!getCachedBillboardUrl();
        refreshLoadingConfig().then((url) => {
            if (cancelled || !url || hadCache) return;
            const img = new Image();
            img.onload = () => {
                if (!cancelled && Date.now() - mountedAt <= BILLBOARD_SWAP_WINDOW_MS) setBillboardSrc(url);
            };
            img.src = url;
        });
        return () => { cancelled = true; };
    }, [mountedAt]);

    useEffect(() => {
        let cancelled = false;
        let holdTimer = null;

        const finish = () => {
            if (cancelled || completedRef.current) return;
            const holdLeft = Math.max(0, MIN_SHOW_MS - (Date.now() - mountedAt));
            holdTimer = setTimeout(() => {
                if (cancelled || completedRef.current) return;
                completedRef.current = true;
                onCompleteRef.current?.();
                setFading(true);
            }, holdLeft);
        };

        // 1️⃣ Priority images
        const priorityImages = [
            IMAGES.SHARED.EmptyBG,
            IMAGES.SHARED.GCLogoWhite,
        ].filter(Boolean);

        // 2️⃣ Remaining images (excluding priority)
        const imgList = Array.isArray(imagesToLoad)
            ? imagesToLoad.filter(src => !priorityImages.includes(src))
            : [];

        const fontList = Array.isArray(fontsToLoad) ? fontsToLoad : [];
        const audioList = Array.isArray(audioToLoad) ? audioToLoad : [];

        // ---- Loaders ----
        const loadImage = src =>
            new Promise(res => {
                const img = new Image();
                img.onload = img.onerror = () => res();
                img.src = src;
            });

        const loadFont = font =>
            new Promise(res => {
                if (!window.FontFace) return res();
                const fontFace = new FontFace(font.font, `url(${font.path})`);
                fontFace.load().then(
                    () => { document.fonts.add(fontFace); res(); },
                    () => res()
                );
            });

        const loadAudio = def =>
            audio.primeClip(def).catch(() => {});

        // ---- Sequential pipeline ----
        const loadSequentially = async () => {
            try {
                // Priority images FIRST
                for (const src of priorityImages) {
                    if (cancelled) return;
                    await loadImage(src);
                }

                // Remaining images
                for (const src of imgList) {
                    if (cancelled) return;
                    await loadImage(src);
                }

                // Fonts
                for (const font of fontList) {
                    if (cancelled) return;
                    await loadFont(font);
                }

                // Audio
                for (const clip of audioList) {
                    if (cancelled) return;
                    await loadAudio(clip);
                }
            } catch {
                // A failed asset must not trap the user on the loading screen.
            }
            finish();
        };

        loadSequentially();

        return () => { cancelled = true; clearTimeout(holdTimer); };
    }, [imagesToLoad, fontsToLoad, audioToLoad, mountedAt]);

    useEffect(() => {
        if (!fading) return;
        const t = setTimeout(() => setDone(true), FADE_MS + 50);
        return () => clearTimeout(t);
    }, [fading]);

    if (done) return null;

    return (
        <div className={`${styles.preloader} ${fading ? styles.fadeOut : ""}`} aria-busy="true" role="status">
            <GameCityLoading text="Loading" billboardSrc={billboardSrc || undefined} />
        </div>
    );
}

export { flatten };
